import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../api/client';
import { IconCircleCheckFilled, IconClockFilled, IconBowlChopsticksFilled } from '@tabler/icons-react';
import PedidoTiempo from '../Utils/PedidoTiempo';
import Switch from '../Utils/Switch';

const FONT = '"Public Sans", sans-serif';

function StatusBadge({ done }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 h-7 rounded-full px-3"
      style={{
        backgroundColor: done ? 'rgba(48, 209, 88, 0.12)' : 'rgba(248, 124, 43, 0.12)',
        fontSize: '12px',
        fontWeight: 600,
        color: done ? '#30d158' : '#f87c2b',
        fontFamily: FONT,
      }}
    >
      {done ? <IconCircleCheckFilled size={14} /> : <IconClockFilled size={14} />}
      {done ? 'Realizado' : 'Pendiente'}
    </span>
  );
}

export default function AdminCocina({ notify }) {
  const [orders, setOrders] = useState([]);

  const fetchOrders = useCallback(async () => {
    try {
      const res = await apiFetch(`/sales/cocina`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const mapped = (data || []).map((sale) => ({
        id: sale.id,
        code: sale.code_order,
        customer: sale.customer_name || 'Cliente mostrador',
        time: new Date(sale.sale_date).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }),
        startTime: new Date(sale.sale_date).getTime(),
        items: (sale.items || []).map((it) => ({
          id: it.id,
          name: it.item_name,
          status: it.status === 'done' ? 'done' : 'pending',
          ingredients: [],
          variants: (it.variants || []).map((v) => ({ group: v.group_name, option: v.option_name })),
        })),
      }));
      setOrders(mapped);
    } catch (e) {
      console.error('Error al cargar pedidos:', e);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);
  const [detailsOrder, setDetailsOrder] = useState(null);

  const toggleItem = async (orderId, itemId, nextStatus) => {
    try {
      await apiFetch(`/sales/items/${itemId}/status?status=${nextStatus}`, { method: 'PATCH' });
    } catch (e) {
      console.error('Error al actualizar estado:', e);
    }
    setOrders((prev) =>
      prev.map((o) =>
        o.id !== orderId
          ? o
          : {
              ...o,
              items: o.items.map((it) =>
                it.id === itemId ? { ...it, status: nextStatus } : it
              ),
            }
      )
    );
  };

  return (
    <div className="flex flex-col h-screen bg-black overflow-hidden">
      <header className="shrink-0 px-4 lg:px-6 pt-5 pb-4 border-b border-white/[0.16]">
        <h1 className="font-semibold m-0" style={{ fontSize: '22px', lineHeight: '28px', letterSpacing: '-0.02em', color: 'rgba(255,255,255,0.95)', fontFamily: FONT }}>
          Cocina
        </h1>
        <p className="m-0 mt-1" style={{ fontSize: '13px', lineHeight: '18px', fontWeight: 500, color: 'rgba(255,255,255,0.45)', fontFamily: FONT }}>
          Platillos por preparar y entregar
        </p>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto pedidos-scroll px-4 lg:px-6 py-5">
        <div className="grid gap-4 content-start" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))' }}>
          {orders.map((order) => {
            const allDone = order.items.length > 0 && order.items.every((it) => it.status === 'done');
            return (
            <div key={order.id} className="rounded-3xl p-4 flex flex-col gap-3" style={{ backgroundColor: 'rgba(255,255,255,0.035)', opacity: allDone ? 0.45 : 1, filter: allDone ? 'grayscale(0.5)' : 'none', transition: 'opacity 0.2s ease, filter 0.2s ease' }}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="font-semibold m-0 truncate" style={{ fontSize: '16px', lineHeight: '21px', color: 'rgba(255,255,255,0.95)', fontFamily: FONT }}>
                    {order.code} · {order.customer}
                  </h2>
                  <p className="m-0 mt-0.5" style={{ fontSize: '12px', lineHeight: '16px', fontWeight: 500, color: 'rgba(255,255,255,0.4)', fontFamily: FONT, fontVariantNumeric: 'tabular-nums' }}>
                    {order.time}
                  </p>
                </div>
                <div className="shrink-0 flex items-center gap-2">
                  <PedidoTiempo startTime={order.startTime} icon={IconBowlChopsticksFilled} />
                  <button
                    type="button"
                    onClick={() => setDetailsOrder(order)}
                    className="shrink-0 h-9 px-3 rounded-full border-none cursor-pointer font-semibold transition-all duration-200 ease-out active:scale-[0.98] hover:bg-white/10"
                    style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.9)', fontSize: '12px', fontFamily: FONT }}
                  >
                    Ver detalles
                  </button>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                {order.items.map((item) => {
                  const done = item.status === 'done';
                  return (
                  <div key={item.id} className="flex items-center justify-between gap-3 rounded-2xl px-3 py-2.5" style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}>
                    <div className="min-w-0 flex-1" style={{ opacity: done ? 0.3 : 1, filter: done ? 'grayscale(0.7)' : 'none', transition: 'opacity 0.2s ease, filter 0.2s ease' }}>
                      <p className="m-0 truncate" style={{ fontSize: '14px', lineHeight: '19px', fontWeight: 600, color: 'rgba(255,255,255,0.9)', fontFamily: FONT }}>
                        {item.name}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <StatusBadge done={done} />
                      <Switch
                        showLabel={false}
                        defaultOn={done}
                        onChange={(val) => toggleItem(order.id, item.id, val ? 'done' : 'pending')}
                      />
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
            );
          })}
        </div>
      </div>

      {detailsOrder && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-3"
          style={{ backgroundColor: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
          onClick={() => setDetailsOrder(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            className="flex flex-col overflow-hidden w-full"
            style={{ backgroundColor: '#000', borderRadius: '28px', border: '1px solid rgba(255,255,255,0.16)', maxWidth: '720px', maxHeight: 'min(700px, calc(100dvh - 24px))', boxShadow: '0 40px 120px rgba(0,0,0,0.8)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="shrink-0 px-5 pt-5 pb-4 border-b border-white/[0.16]">
              <h2 className="font-semibold m-0" style={{ fontSize: '20px', lineHeight: '26px', letterSpacing: '-0.02em', color: 'rgba(255,255,255,0.95)', fontFamily: FONT }}>
                Detalles de cocina
              </h2>
              <p className="m-0 mt-1" style={{ fontSize: '13px', lineHeight: '18px', fontWeight: 500, color: 'rgba(255,255,255,0.45)', fontFamily: FONT }}>
                {detailsOrder.code} · {detailsOrder.customer}
              </p>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto pedidos-scroll px-5 py-4 flex flex-col gap-4">
              {detailsOrder.items.map((item) => (
                <div key={item.id} className="rounded-2xl p-4" style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}>
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="font-semibold m-0" style={{ fontSize: '15px', lineHeight: '20px', color: 'rgba(255,255,255,0.95)', fontFamily: FONT }}>
                      {item.name}
                    </h3>
                    <StatusBadge done={item.status === 'done'} />
                  </div>
                  <div className="mt-3">
                    <p className="m-0 mb-2" style={{ fontSize: '12px', lineHeight: '16px', fontWeight: 600, color: 'rgba(255,255,255,0.4)', fontFamily: FONT, letterSpacing: '0.01em' }}>
                      INGREDIENTES
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {item.ingredients.map((ing, i) => (
                        <span key={i} className="inline-flex items-center h-7 px-3 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.06)', fontSize: '12px', fontWeight: 500, color: 'rgba(255,255,255,0.8)', fontFamily: FONT }}>
                          {ing}
                        </span>
                      ))}
                    </div>
                  </div>
                  {item.variants?.length > 0 && (
                    <div className="mt-3 flex flex-col gap-2">
                      {item.variants.map((v, i) => (
                        <div key={i} className="flex items-center justify-between gap-3">
                          <span style={{ fontSize: '12px', lineHeight: '16px', fontWeight: 600, color: 'rgba(255,255,255,0.4)', fontFamily: FONT, letterSpacing: '0.01em' }}>
                            {v.group}
                          </span>
                          <span className="inline-flex items-center justify-center rounded-full px-3" style={{ height: '24px', backgroundColor: 'rgba(255,255,255,0.08)', fontSize: '12px', fontWeight: 600, color: 'rgba(255,255,255,0.85)', fontFamily: FONT, whiteSpace: 'nowrap' }}>
                            {v.option}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex items-center justify-end shrink-0 px-5 py-4 border-t border-white/[0.16]">
              <button
                type="button"
                onClick={() => setDetailsOrder(null)}
                className="flex items-center justify-center h-12 px-6 rounded-full border-none cursor-pointer font-semibold transition-all duration-200 ease-out active:scale-[0.98] hover:bg-white/90"
                style={{ backgroundColor: '#fff', color: '#000', fontSize: '14px', letterSpacing: '-0.01em', fontFamily: FONT }}
              >
                Regresar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
